import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Typography,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  CircularProgress,
  useTheme,
  Stack
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useSnackbar } from '../../providers/SnackBarProvider';
import { useAppBar } from '../../providers/AppBarProvider';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { handleError } from '../../utils/lib';
import { useTranslation } from 'react-i18next';
import VetActionsBottomSheet from '../../layouts/VetActionsBottomSheet';

const ViewVetServicesSchedules = () => {
  const snackbar = useSnackbar();
  const navigate = useNavigate();
  const { updateTitle } = useAppBar();
  const [servicesSchedules, setServicesSchedules] = useState([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [idToDelete, setIdToDelete] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const { t } = useTranslation();

  const translateWeekday = (weekday) => {
    if (!weekday) return weekday;
    return t(`agenda.${weekday.toLowerCase()}`, { defaultValue: weekday });
  };

  useEffect(() => {
    updateTitle('', t('view.vetActionServicesSchedules'));
    fetchServicesSchedules();
  }, [t]);

  const fetchServicesSchedules = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(
        `${import.meta.env.VITE_API_URL}vets?schedules`,
        { withCredentials: true }
      );
      setServicesSchedules(response.data.data || []);
      //console.log(response.data.data);
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = (item) => {
    setIdToDelete(item.id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    try {
      setLoading(true);
      await axios.delete(
        `${import.meta.env.VITE_API_URL}vets?delete_schedule&id=${idToDelete}`,
        { withCredentials: true }
      );
      setServicesSchedules(servicesSchedules.filter(s => s.id !== idToDelete));
    } catch (error) {
      handleError(error, snackbar);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setIdToDelete(null);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" >
      <Paper sx={{ p: 2 }}>
        <List>
          {servicesSchedules.map((service, index) => (
            <React.Fragment key={service.id}>
              <ListItem>
                <ListItemText
                  primary={
                    <>
                      {service.service.name}
                      {service.starting_hour && service.ending_hour && (
                        <>
                          {' '}
                          {t('view.serviceScheduleHours', {
                            weekday: translateWeekday(service.weekday),
                            start: service.starting_hour,
                            end: service.ending_hour,
                          })}
                        </>
                      )}
                      {service.user && <> - {service.user.name}</>}
                    </>
                  }
                />
                <ListItemSecondaryAction>
                  <IconButton
                    edge="end"
                    size="small"
                    onClick={() => handleDelete(service)}
                    color="default"
                  >
                    <DeleteIcon />
                  </IconButton>
                </ListItemSecondaryAction>
              </ListItem>
              {index < servicesSchedules.length - 1 && <Divider />}
            </React.Fragment>
          ))}
        </List>
      </Paper>

      {/* Action Buttons */}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'center' }}>
        <Stack direction="row" spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/main/edit-vet-service-assoc')}
          >
            {t('view.editVetServices')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/main/edit-vet-schedule')}
          >
            {t('view.createSchedule')}
          </Button>
        </Stack>
      </Box>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          {t('common.confirmDeletion')}
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            {t('view.confirmDeleteSchedule')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={confirmDelete} color="error" autoFocus disabled={loading}>
            {t('common.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <VetActionsBottomSheet />
    </Container>
  );
};

export default ViewVetServicesSchedules;
